import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMeQuery, useSetPreferredLanguageMutation } from "generated/graphql";
import { Language, toLanguage } from "locales/constants";
import { rememberLanguage, storedLanguage } from "locales/preference";

/** Switches language, remembering it on this device and on the account. */
export const useChooseLanguage = () => {
  const { i18n } = useTranslation();
  const [{ data }] = useMeQuery();
  const [, setPreferredLanguage] = useSetPreferredLanguageMutation();
  const signedIn = Boolean(data?.me);

  return useCallback(
    (language: Language) => {
      rememberLanguage(language);
      i18n.changeLanguage(language);
      if (signedIn) {
        setPreferredLanguage({ language });
      }
    },
    [i18n, signedIn, setPreferredLanguage],
  );
};

/**
 * Applies the account's language once it loads, or saves the one picked
 * before signing in. `fallback` (a portal's default) applies only when
 * neither exists.
 */
export const useLanguageSync = (fallback?: string | null) => {
  const { i18n } = useTranslation();
  const [{ data }] = useMeQuery();
  const [, setPreferredLanguage] = useSetPreferredLanguageMutation();
  const me = data?.me;

  useEffect(() => {
    const picked = storedLanguage();
    const apply = (code: string) => {
      const language = toLanguage(code);
      if (toLanguage(i18n.language) !== language) {
        i18n.changeLanguage(language);
      }
    };

    if (me?.preferredLanguage) {
      apply(me.preferredLanguage);
    } else if (me && picked) {
      setPreferredLanguage({ language: picked });
    } else if (!picked && fallback) {
      apply(fallback);
    }
  }, [me, fallback, i18n, setPreferredLanguage]);
};
